import { useMemo, useState } from "react";
import { UploadCloud } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { DocumentAttachmentsUpload } from "@/components/medical-records/DocumentAttachmentsUpload";
import { useExamAttachments } from "@/hooks/useExamAttachments";

interface PetExamUploadDialogProps {
  petId: string;
  clinicId: string | null | undefined;
  triggerLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderTrigger?: boolean;
}

export function PetExamUploadDialog({
  petId,
  clinicId,
  triggerLabel,
  open,
  onOpenChange,
  renderTrigger = true,
}: PetExamUploadDialogProps) {
  const { t } = useTranslation("patients");
  const [internalOpen, setInternalOpen] = useState(false);
  const { uploadAttachments } = useExamAttachments(petId, undefined, clinicId ?? undefined);

  const isDisabled = useMemo(() => !petId || !clinicId, [petId, clinicId]);
  const isControlled = typeof open === "boolean";
  const resolvedOpen = isControlled ? open : internalOpen;

  const setOpenState = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const resolvedTriggerLabel = triggerLabel ?? t("petDocumentDialog.trigger");

  return (
    <ResponsiveDialog
      open={resolvedOpen}
      onOpenChange={setOpenState}
      trigger={
        renderTrigger ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDisabled}
            className="gap-2"
          >
            <UploadCloud className="h-4 w-4" />
            {resolvedTriggerLabel}
          </Button>
        ) : undefined
      }
      className="flex max-h-[min(90vh,48rem)] max-w-3xl flex-col overflow-hidden"
    >
      <DialogHeader>
        <DialogTitle>{t("petDocumentDialog.title")}</DialogTitle>
      </DialogHeader>
      <DocumentAttachmentsUpload
        clinicId={clinicId}
        onUpload={async (items) => {
          await uploadAttachments(items);
          setOpenState(false);
        }}
      />
    </ResponsiveDialog>
  );
}
